import { useAddMentorCommentToFlag } from '@actions';
import { ReactComponent as BlockIcon } from '@assets/icons/V4/block-icon.svg';
import Button from '@components/V4/Button';
import Pagination, { PaginationProps } from '@components/V4/Pagination';
import SkeletonBox from '@components/V4/SkeletonBox';
import { Flag, FLAG_STATUS, FlagTypes } from '@shared/common';
import { BASE_URL } from '@shared/frontendEnv';
import EmptyStateCard from 'apps/agora/src/views/UserView/components/EmptyStateCard';
import moment from 'moment';
import { memo, useCallback, useMemo, useState } from 'react';
import AddCommentModal from './AddCommentModal';

interface MyFlagsMobileCardProps {
  refetch: () => void;
  flags?: Flag[];
  pagination: PaginationProps;
  isLoading: boolean;
}

interface MyFlagsMobileItemProps {
  flag: any;
  onAddComment: (flagId: string) => void;
  viewHandler: () => void;
}

const getStudentName = (flagData: any) => {
  return (
    flagData?.ref?.students?.[0]?.fullName ??
    flagData?.ref?.user?.fullName ??
    `${flagData?.ref?.firstName ?? '-'} ${flagData?.ref?.lastName ?? ''}`.trim()
  );
};

const MyFlagsMobileItem = memo(({ flag, onAddComment, viewHandler }: MyFlagsMobileItemProps) => {
  const isClosed = !!flag?.closedAt;
  const hasMentorComment = !!flag?.mentorComment;

  return (
    <div className="flex flex-col gap-3 p-4 bg-surfaceObject rounded-2xl">
      <div className="text-sm">
        <span className="text-white">{flag?.description ?? '-'}</span>
      </div>

      <div className="flex flex-col gap-1 text-xs text-customGrey">
        <div className="flex justify-between gap-2">
          <span className="font-medium text-white">Student</span>
          <span className="truncate">{getStudentName(flag)}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="font-medium text-white">Course</span>
          <span className="truncate">{flag?.ref?.course?.title ?? '-'}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="font-medium text-white">Created</span>
          <span>{flag?.createdAt ? moment(flag.createdAt).format('DD.MM.YYYY') : '-'}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="font-medium text-white">Status</span>
          <span>{FLAG_STATUS[flag?.status]}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="font-medium text-white">Comment</span>
          <span className="truncate">{flag?.mentorComment ?? '-'}</span>
        </div>
      </div>

      <div className="pt-1">
        <Button variant="secondary" onClick={() => viewHandler()} buttonText="View" />
        {!isClosed && !hasMentorComment && (
          <Button buttonText="Add Comment" onClick={() => onAddComment(flag._id)} />
        )}
      </div>
    </div>
  );
});

const MyFlagsMobileCard = ({ flags, refetch, pagination, isLoading }: MyFlagsMobileCardProps) => {
  const [selectedFlag, setSelectedFlag] = useState<Flag | null>(null);
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState(false);

  const flagId = selectedFlag?._id;
  const { mutate: addComment, isLoading: isSaving } = useAddMentorCommentToFlag(flagId as string);

  const openModal = useCallback(
    (id: string) => {
      const flag = flags?.find((f) => f._id === id) || null;
      setSelectedFlag(flag);
      setIsAddCommentModalOpen(true);
    },
    [flags]
  );

  const closeModal = useCallback(() => {
    setIsAddCommentModalOpen(false);
    setSelectedFlag(null);
  }, []);

  const handleSave = useCallback(
    (comment: string) => {
      if (!selectedFlag) return;
      addComment({ comment } as any, {
        onSuccess: () => {
          closeModal();
          refetch();
        },
        onError: () => {},
      });
    },
    [addComment, closeModal, refetch, selectedFlag]
  );

  const viewFlag = useCallback((flag: Flag) => {
    switch (flag.flagType) {
      case FlagTypes.FIRST_MEETING_NOT_SCHEDULED_IN_5_DAYS:
        window.location.href = `${BASE_URL}/session-details/${
          (flag.ref as any)?.sessions?.[0]?._id
        }`;
        break;
      case FlagTypes.FIRST_MEETING_NOT_HELD_IN_10_DAYS:
        window.location.href = `${BASE_URL}/session-details/${
          (flag.ref as any)?.sessions?.[0]?._id
        }`;
        break;
      case FlagTypes.NEXT_MEETING_FURTHER_THAN_62_DAYS:
        window.location.href = `${BASE_URL}/session-details/${(flag.ref as any)?.sessionId}`;
        break;
      case FlagTypes.NO_MEETING_SCHEDULED_AFTER_48_HOURS_SIENCE_LAST_MEETING_HELD:
        window.location.href = `${BASE_URL}/session-details/${(flag.ref as any)?.sessionId}`;
        break;
      case FlagTypes.NO_FEEDBACK_AFTER_48_HOURS_SINCE_MEETING_HELD:
        window.location.href = `${BASE_URL}/session-details/${(flag.ref as any)?.sessionId}`;
        break;
      case FlagTypes.MAX_DURATION_COURSE_EXCEEDED:
        window.location.href = `${BASE_URL}/my-enrollments?page=1&limit=10&course=${
          (flag.ref as any)?.course?._id
        }&studentSearch=${(flag.ref as any)?.user?.fullName}`;
        break;

      case FlagTypes.PAST_MEETING_WITH_INVALID_STATUS:
        window.location.href = `${BASE_URL}/session-details/${(flag.ref as any)?.sessionId}`;
        break;

      case FlagTypes.ENROLLMENT_NOT_ENDED_AFTER_LAST_SESSION:
        window.location.href = `${BASE_URL}/my-enrollments?page=1&limit=10&course=${
          (flag.ref as any)?.course?._id
        }&studentSearch=${(flag.ref as any)?.students[0]?.fullName}`;
        break;

      case FlagTypes.BILLABLE_TIME_LESS_THAN_50_MINUTES:
        window.location.href = `${BASE_URL}/session-details/${(flag.ref as any)?.sessionId}`;
        break;

      default:
        console.warn('Unknown flag type:', flag.flagType);
        break;
    }
  }, []);

  const items = useMemo(
    () =>
      flags?.map((flag) => (
        <MyFlagsMobileItem
          key={flag._id}
          flag={flag}
          onAddComment={openModal}
          viewHandler={() => viewFlag(flag)}
        />
      )),
    [flags, openModal]
  );

  return (
    <>
      <section className="flex flex-col gap-6 tablet:hidden">
        {isLoading
          ? [...Array(8).keys()].map((i) => (
              <SkeletonBox key={i} className="h-28 w-full rounded-2xl" />
            ))
          : items}

        {!isLoading && !flags?.length && (
          <EmptyStateCard
            className="h-64"
            icon={<BlockIcon className="text-customGrey" />}
            text="No results."
          />
        )}

        {!!pagination && (pagination as any)?.size && (
          <Pagination size={'default'} {...pagination} />
        )}
      </section>

      <AddCommentModal
        isOpen={isAddCommentModalOpen}
        flag={selectedFlag}
        saving={isSaving}
        onClose={closeModal}
        onSave={handleSave}
      />
    </>
  );
};

export default MyFlagsMobileCard;
